/**
 * Payment Telemetry React Hooks
 *
 * Modern React hooks for instrumenting payment processing with comprehensive
 * OpenTelemetry payment event tracking. Provides seamless integration with
 * existing payment flows while maintaining PCI compliance.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  type PaymentContext,
  type PaymentError,
  type PaymentErrorCategory,
  type PaymentEventType,
  type PaymentMethodType,
  paymentTelemetry,
} from './payment-telemetry'

// Hook for managing payment correlation IDs and context
export function usePaymentContext(initialContext?: Partial<PaymentContext>) {
  const [context, setContext] = useState<PaymentContext>(() => ({
    correlationId: paymentTelemetry.generateCorrelationId(),
    processor: initialContext?.processor || 'unknown',
    method: initialContext?.method || 'unknown',
    ...initialContext,
  }))

  const updateContext = useCallback((updates: Partial<PaymentContext>) => {
    setContext((prev) => ({ ...prev, ...updates }))
  }, [])

  const createNewCorrelation = useCallback(() => {
    const newCorrelationId = paymentTelemetry.generateCorrelationId()
    setContext((prev) => ({ ...prev, correlationId: newCorrelationId }))
    return newCorrelationId
  }, [])

  return {
    context,
    updateContext,
    createNewCorrelation,
  }
}

// Hook for tracking payment events throughout component lifecycle
export function usePaymentEventLogger(paymentContext: PaymentContext) {
  const logEvent = useCallback(
    (
      eventType: PaymentEventType,
      error?: PaymentError,
      additionalAttributes?: Record<string, string | number | boolean>,
    ) => {
      paymentTelemetry.logPaymentEvent(eventType, paymentContext, error, additionalAttributes)
    },
    [paymentContext],
  )

  const logError = useCallback(
    (error: PaymentError, additionalAttributes?: Record<string, string | number | boolean>) => {
      paymentTelemetry.logPaymentError(error, paymentContext, additionalAttributes)
    },
    [paymentContext],
  )

  const logSuccess = useCallback(
    (
      transactionId?: string,
      amount?: number,
      currency?: string,
      additionalAttributes?: Record<string, string | number | boolean>,
    ) => {
      const attributes = {
        ...additionalAttributes,
        ...(transactionId && { 'payment.transaction.id': transactionId }),
        ...(amount && { 'payment.amount': amount }),
        ...(currency && { 'payment.currency': currency }),
      }

      logEvent('payment.success', undefined, attributes)
    },
    [logEvent],
  )

  return {
    logEvent,
    logError,
    logSuccess,
  }
}

// Hook for payment form validation with telemetry
export function usePaymentValidation(paymentContext: PaymentContext) {
  const { logEvent } = usePaymentEventLogger(paymentContext)

  const validateAndLog = useCallback(
    (
      fieldName: string,
      value: unknown,
      validator: (value: unknown) => { isValid: boolean; error?: string },
      additionalAttributes?: Record<string, string | number | boolean>,
    ) => {
      const result = validator(value)

      if (!result.isValid && result.error) {
        const validationError: PaymentError = {
          category: 'validation',
          code: `VALIDATION_${fieldName.toUpperCase()}_INVALID`,
          message: result.error,
          recoverable: true,
        }

        logEvent('payment.validation.error', validationError, {
          'validation.field': fieldName,
          'validation.error_type': 'client_side',
          ...additionalAttributes,
        })
      }

      return result
    },
    [logEvent],
  )

  return {
    validateAndLog,
  }
}

// Hook for payment operation timing and performance tracking
export function usePaymentOperation(paymentContext: PaymentContext) {
  const operationStartTime = useRef<number>(Date.now())
  const [isLoading, setIsLoading] = useState(false)
  const { logEvent, logError } = usePaymentEventLogger(paymentContext)

  const executeWithTelemetry = useCallback(
    async <T>(
      operationName: string,
      operation: () => Promise<T>,
      eventType: PaymentEventType = 'payment.attempt',
    ): Promise<T> => {
      setIsLoading(true)
      operationStartTime.current = Date.now()

      try {
        // Log operation start
        logEvent(eventType, undefined, {
          'payment.operation': operationName,
          'payment.operation.start_time': operationStartTime.current,
        })

        // Execute operation with span tracking
        const result = await paymentTelemetry.createPaymentSpan(operationName, paymentContext, operation)

        // Log successful completion
        const responseTime = operationStartTime.current ? Date.now() - operationStartTime.current : 0
        logEvent('payment.success', undefined, {
          'payment.operation': operationName,
          'payment.response.time_ms': responseTime,
          'payment.operation.completed': true,
        })

        return result
      } catch (error) {
        // Log operation failure
        const responseTime = operationStartTime.current ? Date.now() - operationStartTime.current : 0

        const paymentError: PaymentError = {
          category: categorizePaymentError(error),
          message: error instanceof Error ? error.message : 'Unknown error',
          recoverable: isRecoverablePaymentError(error),
          responseTimeMs: responseTime,
          originalError:
            error instanceof Error
              ? {
                  name: error.name,
                  message: error.message,
                  stack: error.stack,
                }
              : undefined,
        }

        logError(paymentError, {
          'payment.operation': operationName,
          'payment.response.time_ms': responseTime,
          'payment.operation.failed': true,
        })

        throw error
      } finally {
        setIsLoading(false)
      }
    },
    [paymentContext, logEvent, logError],
  )

  return {
    executeWithTelemetry,
    isLoading,
  }
}

// Hook for ProPay enrollment tracking
export function useProPayEnrollment(merchantId: string, sourceEmail: string) {
  const { context, updateContext } = usePaymentContext({
    processor: 'propay',
    method: 'credit_card',
    merchantId,
    propaySourceEmail: sourceEmail,
  })

  const { logEvent, logError } = usePaymentEventLogger(context)

  const logEnrollmentStep = useCallback(
    (step: string, additionalAttributes?: Record<string, string | number | boolean>) => {
      updateContext({ propayEnrollmentStep: step })

      logEvent('propay.enrollment.start', undefined, {
        'propay.enrollment.step': step,
        'propay.enrollment.merchant_id': merchantId,
        'propay.enrollment.source_email': sourceEmail,
        ...additionalAttributes,
      })
    },
    [updateContext, logEvent, merchantId, sourceEmail],
  )

  const logEnrollmentError = useCallback(
    (
      step: string,
      error: Error | string,
      errorType: string,
      additionalAttributes?: Record<string, string | number | boolean>,
    ) => {
      const paymentError: PaymentError = {
        category: 'processor',
        code: `PROPAY_ENROLLMENT_${step.toUpperCase()}_ERROR`,
        message: typeof error === 'string' ? error : error.message,
        recoverable: true,
        propayErrorType: errorType,
      }

      updateContext({
        propayEnrollmentStep: step,
        propayAccountId: undefined, // Clear account ID on error
      })

      logEvent('propay.enrollment.error', paymentError, {
        'propay.enrollment.step': step,
        'propay.enrollment.merchant_id': merchantId,
        'propay.enrollment.source_email': sourceEmail,
        'propay.error.type': errorType,
        ...additionalAttributes,
      })
    },
    [updateContext, logEvent, merchantId, sourceEmail],
  )

  const logEnrollmentSuccess = useCallback(
    (step: string, propayAccountId: string, additionalAttributes?: Record<string, string | number | boolean>) => {
      updateContext({
        propayEnrollmentStep: step,
        propayAccountId,
      })

      logEvent('propay.enrollment.success', undefined, {
        'propay.enrollment.step': step,
        'propay.enrollment.merchant_id': merchantId,
        'propay.enrollment.source_email': sourceEmail,
        'propay.account.id': propayAccountId,
        ...additionalAttributes,
      })
    },
    [updateContext, logEvent, merchantId, sourceEmail],
  )

  return {
    context,
    updateContext,
    logEnrollmentStep,
    logEnrollmentError,
    logEnrollmentSuccess,
  }
}

// Hook for payment retry logic with exponential backoff tracking
export function usePaymentRetry(paymentContext: PaymentContext, maxRetries: number = 3, baseDelay: number = 1000) {
  const [retryCount, setRetryCount] = useState(0)
  const [isRetrying, setIsRetrying] = useState(false)
  const { logEvent } = usePaymentEventLogger(paymentContext)

  const executeWithRetry = useCallback(
    async <T>(
      operation: () => Promise<T>,
      shouldRetry: (error: unknown) => boolean = isRecoverablePaymentError,
    ): Promise<T> => {
      let lastError: unknown

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          if (attempt > 0) {
            setIsRetrying(true)
            const delay = baseDelay * Math.pow(2, attempt - 1)

            logEvent('payment.retry', undefined, {
              'payment.retry.attempt': attempt,
              'payment.retry.max_retries': maxRetries,
              'payment.retry.delay_ms': delay,
              'payment.retry.total_attempts': retryCount + 1,
            })

            await new Promise((resolve) => setTimeout(resolve, delay))
          }

          const result = await operation()

          if (attempt > 0) {
            setRetryCount(0)
            setIsRetrying(false)
          }

          return result
        } catch (error) {
          lastError = error

          if (attempt === maxRetries || !shouldRetry(error)) {
            // Final failure - log error
            const paymentError: PaymentError = {
              category: categorizePaymentError(error),
              message: error instanceof Error ? error.message : 'Unknown error',
              recoverable: false,
              originalError:
                error instanceof Error
                  ? {
                      name: error.name,
                      message: error.message,
                      stack: error.stack,
                    }
                  : undefined,
            }

            logEvent('payment.error', paymentError, {
              'payment.retry.final_attempt': true,
              'payment.retry.total_attempts': attempt + 1,
              'payment.retry.max_retries': maxRetries,
              'payment.retry.exhausted': attempt === maxRetries,
            })

            setRetryCount(0)
            setIsRetrying(false)
            break
          }

          setRetryCount((prev) => prev + 1)
        }
      }

      throw lastError
    },
    [maxRetries, baseDelay, retryCount, logEvent],
  )

  return {
    executeWithRetry,
    retryCount,
    isRetrying,
    resetRetryCount: () => setRetryCount(0),
  }
}

// Utility functions for error categorization
function categorizePaymentError(error: unknown): PaymentErrorCategory {
  if (!error) return 'system'

  const errorMessage = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase()

  if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
    return errorMessage.includes('timeout') ? 'timeout' : 'network'
  }

  if (errorMessage.includes('validation') || errorMessage.includes('invalid')) {
    return 'validation'
  }

  if (errorMessage.includes('unauthorized') || errorMessage.includes('forbidden')) {
    return 'authorization'
  }

  if (errorMessage.includes('declined') || errorMessage.includes('processor')) {
    return 'processor'
  }

  if (errorMessage.includes('cancelled')) {
    return 'user_cancelled'
  }

  return 'system'
}

function isRecoverablePaymentError(error: unknown): boolean {
  if (!error) return false

  const errorMessage = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase()

  // Non-recoverable errors
  const nonRecoverablePatterns = ['declined', 'insufficient funds', 'invalid card', 'expired', 'forbidden', 'cancelled']

  if (nonRecoverablePatterns.some((pattern) => errorMessage.includes(pattern))) {
    return false
  }

  // Recoverable errors
  const recoverablePatterns = ['timeout', 'network', 'temporary', 'retry', 'server error']

  return recoverablePatterns.some((pattern) => errorMessage.includes(pattern))
}

// Export all hooks and utilities
export { categorizePaymentError, isRecoverablePaymentError }
